import { format } from 'date-fns'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { PushPin } from '@mui/icons-material'
import { Box, Card, Chip, Stack, TableContainer, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useDeleteEditorialCardMutation, useGetEditorialCardsQuery } from '../../api/editorial-cards'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { editorialCardStatusLabelKeys, filterEditorialCards, getEditorialCardStatus } from '../../features/editorial-cards/helpers/editorialCardHelpers'
import { EditorialCard } from '../../features/editorial-cards/types/EditorialCard'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = { now: number; onEdit: (card: EditorialCard) => void; onDelete: (card: EditorialCard) => void }

const statusChipColors = { published: 'success', scheduled: 'info', draft: 'default' } as const

export const editorialCardEditPath = (id?: string) => `/internal/editorial-cards/edit${id ? `/${id}` : ''}`

export const EditorialCardsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-editorial-cards'))
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const cardsQuery = useGetEditorialCardsQuery()
  const [deleteCard] = useDeleteEditorialCardMutation()
  const [search, setSearch] = useState('')
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<EditorialCard>>()

  const rows = useMemo(() => filterEditorialCards(cardsQuery.data ?? [], search), [cardsQuery.data, search])

  const tableColumns: GRTableColumn<EditorialCard, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-marketing:editorial_cards_title'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.titles?.en || '---'}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.titles?.en?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 260 } },
      },
      {
        labelAccessor: t('internal-marketing:editorial_cards_created_by'),
        accessor: ({ row }) => row.createdBy || '---',
        sortable: true,
        sortAccessor: ({ row }) => row.createdBy?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: t('internal-marketing:editorial_cards_modified_by'),
        accessor: ({ row }) => row.modifiedBy || '---',
        sortable: true,
        sortAccessor: ({ row }) => row.modifiedBy?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: t('internal-marketing:editorial_cards_published_at'),
        accessor: ({ row, customTableProps }) =>
          getEditorialCardStatus(row, customTableProps?.now) === 'draft' ? '' : format(row.publishedAt, 'dd.MM.yyyy HH:mm'),
        sortable: true,
        sortAccessor: ({ row }) => row.publishedAt,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 120 } },
      },
      {
        labelAccessor: t('internal-marketing:editorial_cards_status'),
        accessor: ({ row, customTableProps }) => {
          const status = getEditorialCardStatus(row, customTableProps?.now)
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color={statusChipColors[status]} label={t(editorialCardStatusLabelKeys[status])} />
              {row.entryData?.sticky && (
                <Tooltip title={t('internal-marketing:editorial_cards_sticky')}>
                  <PushPin fontSize="small" color="primary" aria-label={t('internal-marketing:editorial_cards_sticky')} />
                </Tooltip>
              )}
            </Stack>
          )
        },
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 130 } },
      },
      rowActionsColumn<EditorialCard, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-marketing:editorial_cards_edit'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-marketing:editorial_cards_delete'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  // The time of the last load, so the statuses match the list they were computed for.
  const now = cardsQuery.fulfilledTimeStamp ?? Date.now()
  const customProps = useMemo<CustomProps>(
    () => ({
      now,
      onEdit: (card) => navigate(editorialCardEditPath(card.id)),
      onDelete: (card) => {
        const name = card.titles?.en || '---'
        const isPublished = getEditorialCardStatus(card) !== 'draft'
        setConfirmDialogData({
          title: t('internal-marketing:editorial_cards_delete'),
          confirmText: t(isPublished ? 'internal-marketing:editorial_cards_delete_published_confirm' : 'internal-marketing:editorial_cards_delete_confirm', {
            name,
          }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: card,
        })
      },
    }),
    [navigate, now, t]
  )

  const { refetch } = cardsQuery
  const handleConfirm = useCallback(
    async (confirmed?: ConfirmDialogData<EditorialCard>) => {
      setConfirmDialogData(undefined)
      const card = confirmed?.data
      if (!card) return
      const name = card.titles?.en || '---'
      try {
        await deleteCard(card.id).unwrap()
        const remaining = await refetch().unwrap()
        if (remaining.some((listed) => listed.id === card.id)) {
          notifyError(t('internal-marketing:editorial_cards_not_deleted', { name }))
          return
        }
        notifySuccess(t('internal-marketing:editorial_cards_deleted', { name }))
      } catch {
        notifyError(t('internal-marketing:editorial_cards_delete_failed', { name }))
      }
    },
    [deleteCard, notifyError, notifySuccess, refetch, t]
  )

  return (
    <Box>
      <InternalListToolbar
        search={{ value: search, onChange: setSearch, placeholder: t('internal-marketing:editorial_cards_search') }}
        add={{ label: t('internal-marketing:editorial_cards_add'), onClick: () => navigate(editorialCardEditPath()) }}
      >
        {cardsQuery.data && (
          <Typography variant="body2" color="text.secondary">
            {t('internal-marketing:editorial_cards_count', { count: rows.length, total: cardsQuery.data.length })}
          </Typography>
        )}
      </InternalListToolbar>

      <QueryStateBoundary isLoading={cardsQuery.isLoading} isError={cardsQuery.isError} errorText={t('internal-marketing:editorial_cards_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-marketing:editorial_cards_none')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
